import classNames from 'classnames';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import TextPlugin from 'gsap/dist/TextPlugin';
import { gsap } from 'gsap/dist/gsap';
import Image from 'next/image';
import { FC, useRef } from 'react';
import { useGSAP } from '@gsap/react';

import styles from './b2b-good-for.module.scss';
import { SplitChar } from '../../splitchar/splitchar';
import BusinessButton from '../../business-button/business-button';
import { COLORS } from '../../business-button/business-button.constants';

gsap.registerPlugin(useGSAP);
gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(TextPlugin);

interface Item {
    image: string;
    imageMobile?: string;
    title: string;
    buttonText?: string;
    buttonLink?: string;
    item: {
        content: string;
    }[];
}

export interface B2BGoodForProps {
    backgroundColor?: string;
    textColor?: string;
    content: Item[];
}

const B2BGoodFor: FC<B2BGoodForProps> = (props) => {
    const { backgroundColor = '#E7EAED', textColor = '#000', content = [] } = props;

    const containerRef = useRef<HTMLDivElement>(null);

    useGSAP(
        () => {
            const selector = gsap.utils.selector(containerRef.current);
            const sections = gsap.utils.toArray<HTMLElement>(selector(`.${styles.section}`));

            let mm = gsap.matchMedia();

            mm.add(
                {
                    isMobile: `(max-width: 992px)`,
                    isDesktop: `(min-width: 993px)`,
                },
                (context) => {
                    let isMobile = context.conditions?.isMobile ?? false;

                    gsap.timeline({
                        scrollTrigger: {
                            trigger: containerRef.current,
                            pinnedContainer: containerRef.current,
                            pin: true,
                            start: 'center center',
                            end: '+=100%',
                            scrub: 1,
                        },
                    });

                    let masterTl = gsap.timeline({
                        scrollTrigger: {
                            invalidateOnRefresh: true,
                            trigger: containerRef.current,
                            start: 'top center+=40%',
                            end: 'bottom top',
                            scrub: true,
                        },
                    });

                    gsap.set(sections, { autoAlpha: 0 });

                    for (let i = 0; i < sections.length; i++) {
                        const section = sections[i];

                        const title = isMobile
                            ? gsap.utils.toArray<HTMLDivElement>(gsap.utils.selector(section)(`.${styles.sectionTitle}.${styles.desktopHide}`))[0]
                            : gsap.utils.toArray<HTMLDivElement>(gsap.utils.selector(section)(`.${styles.sectionTitle}.${styles.mobileHide}`))[0];

                        const prvSection = sections[i - 1];
                        if (prvSection) {
                            const prvTitle = isMobile
                                ? gsap.utils.toArray<HTMLDivElement>(gsap.utils.selector(prvSection)(`.${styles.sectionTitle}.${styles.desktopHide}`))[0]
                                : gsap.utils.toArray<HTMLDivElement>(gsap.utils.selector(prvSection)(`.${styles.sectionTitle}.${styles.mobileHide}`))[0];
                            const tlOut = animateSection(prvSection, prvTitle, 'out');
                            masterTl.add(tlOut);
                        }

                        const tlIn = animateSection(section, title, 'in', i === 0 ? '+=1' : '<');
                        masterTl.to(section, { xPercent: -100 * i, x: 0 }, '<');
                        masterTl.add(tlIn, '-=0.5');
                    }
                },
                containerRef
            );
        },
        { scope: containerRef, dependencies: [content] }
    );

    const animateSection = (section: HTMLElement, title: HTMLElement, animationType: 'in' | 'out', position?: gsap.Position) => {
        const sectionSelector = gsap.utils.selector(section);
        const illustrations = gsap.utils.toArray<HTMLDivElement>(sectionSelector(`.${styles.sectionImage}`));
        const contentItems = gsap.utils.toArray<HTMLDivElement>(sectionSelector(`.${styles.sectionDescription} span, a`));
        const titleSplits = gsap.utils.toArray<HTMLDivElement>(gsap.utils.selector(title)(`span`));

        const tl = gsap.timeline({});

        if (animationType === 'in') {
            tl.to(section, { autoAlpha: 1 }, position);
            tl.fromTo(illustrations, { autoAlpha: 0 }, { autoAlpha: 1, stagger: 0.25 }, '<');
            tl.fromTo(
                [titleSplits, contentItems],
                {
                    autoAlpha: 0,
                },
                {
                    duration: 1,
                    autoAlpha: 1,
                    stagger: {
                        from: 'start',
                        each: 0.01,
                    },
                },
                '>-=0.5'
            );
        } else {
            tl.to(
                [titleSplits, contentItems],
                {
                    duration: 0.5,
                    autoAlpha: 0,
                    stagger: {
                        from: 'start',
                        each: 0.005,
                    },
                },
                '+=1'
            );
        }

        return tl;
    };

    return (
        <div ref={containerRef} className={styles.container} style={{ color: textColor, '--bg-color': backgroundColor } as React.CSSProperties}>
            <div className={styles.background} />

            <div className={styles.content}>
                {content.map((section) => {
                    return (
                        <section key={section.title} className={styles.section}>
                            <div className={classNames(styles.sectionTitle, styles.mobileHide)}>
                                <SplitChar text="Good for your&nbsp;" />
                                <SplitChar text={section.title} className={styles.colorMint} />
                            </div>
                            <div className={styles.sectionContent}>
                                <div className={styles.sectionImage}>
                                    <picture>
                                        <source media="(min-width: 640.1px)" srcSet={section.image} />
                                        {section.imageMobile ? <source media="(max-width: 640px)" srcSet={section.imageMobile} /> : null}
                                        <Image
                                            src={section.image || ''}
                                            alt=""
                                            style={{ objectFit: 'cover', objectPosition: 'center' }}
                                            sizes="(max-width: 638px) 98vw,  (max-width: 998px) 43vw, 46vw"
                                            fill={true}
                                            loading={'eager'}
                                            loader={({ src, width, quality }) => {
                                                return `${src}?width=${width}&quality=${quality ?? 75}`;
                                            }}
                                        />
                                    </picture>
                                </div>

                                <div className={classNames(styles.sectionTitle, styles.desktopHide)}>
                                    <SplitChar text="Good for your " />
                                    <span>
                                        <SplitChar text={section.title} />
                                    </span>
                                </div>
                                <div className={styles.sectionDescription}>
                                    {section.item.map(({ content }, index) => {
                                        return <SplitChar key={`${index}`} text={content} className={styles.item} />;
                                    })}
                                    {section.buttonLink && section.buttonText ? (
                                        <BusinessButton
                                            className={styles.sectionButton}
                                            link={section.buttonLink}
                                            buttonText={section.buttonText}
                                            autoMargin={false}
                                            color={COLORS.BLACK}
                                        />
                                    ) : null}
                                </div>
                            </div>
                        </section>
                    );
                })}
            </div>
        </div>
    );
};

export default B2BGoodFor;
